import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, CalendarDays, ChevronRight, ClipboardList, Menu, Shield, Users, LayoutDashboard, LogOut, FileText, Sparkles, X, DatabaseZap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { initials } from "../lib/format";
import api from "../lib/api";

const proNav = [
  { to: "/dashboard", label: "Visão geral", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clientes", icon: Users, testid: "nav-clients" },
  { to: "/weapons", label: "Armas", icon: Shield, testid: "nav-weapons" },
  { to: "/processes", label: "Processos", icon: ClipboardList, testid: "nav-processes" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/documents", label: "Documentos", icon: FileText, testid: "nav-documents" },
];

const clientNav = [
  { to: "/portal", label: "Meu painel", icon: LayoutDashboard, testid: "nav-portal" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isClient = user?.role === "client";
  const menu = isClient ? clientNav : proNav;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const currentLabel = useMemo(() => {
    const item = menu.find(m => location.pathname.startsWith(m.to));
    return item?.label || (isClient ? "Portal do cliente" : "Operação");
  }, [location.pathname, menu, isClient]);

  const handleLogout = async () => {
    await logout();
    toast.success("Sessão encerrada");
    nav("/login", { replace: true });
  };

  const loadDemo = async () => {
    setDemoBusy(true);
    try {
      const { data } = await api.post("/demo/load");
      toast.success("Dados demonstrativos carregados");
      toast.info(`Login cliente demo: ${data.demo_client_login.email}`);
      window.dispatchEvent(new CustomEvent("crafguard:refresh"));
    } catch (e) {
      toast.error("Falha ao carregar dados demo");
    } finally { setDemoBusy(false); }
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="brand">
          <div className="brand-mark"><Shield size={20}/></div>
          <div>
            <b>CRAF<span>GUARD</span></b>
            <small>GESTÃO OPERACIONAL</small>
          </div>
          <button className="mobile-close" onClick={()=>setMobileOpen(false)}><X size={18}/></button>
        </div>
        <div className="workspace-label">{isClient ? "SEU CADASTRO" : "WORKSPACE ATIVO"}</div>
        <div className="workspace">
          <div className="avatar">{initials(user?.name || "?")}</div>
          <div>
            <b>{user?.name || "—"}</b>
            <small>{isClient ? "Portal do cliente" : "Consultoria"}</small>
          </div>
          <ChevronRight size={15}/>
        </div>
        <nav>
          {menu.map(item => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} data-testid={item.testid} className={({isActive}) => isActive ? "active" : ""}>
                <Icon size={18}/>{item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          {!isClient && (
            <button data-testid="load-demo-button" className="demo-button" disabled={demoBusy} onClick={loadDemo}>
              <DatabaseZap size={17}/>{demoBusy ? "Carregando..." : "Carregar dados demo"}
            </button>
          )}
          <button data-testid="logout-button" className="logout" onClick={handleLogout}>
            <LogOut size={17}/>Sair
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="mobile-menu" data-testid="mobile-menu" onClick={()=>setMobileOpen(v=>!v)}><Menu size={20}/></button>
          <div className="breadcrumb">
            Workspace <ChevronRight size={14}/> <strong>{currentLabel}</strong>
          </div>
          <div className="top-actions">
            <button className="icon-button" data-testid="notifications-button" onClick={()=>toast.info("Você tem 3 alertas ativos")}>
              <Bell size={18}/><i/>
            </button>
            <div className="profile">
              {user?.picture ? (
                <img src={user.picture} alt="" className="avatar" style={{objectFit:"cover"}}/>
              ) : (
                <div className="avatar">{initials(user?.name || "?")}</div>
              )}
              <div>
                <b>{user?.name}</b>
                <small>{isClient ? "Cliente" : "Administrador"}</small>
              </div>
            </div>
          </div>
        </header>
        <div className="content">
          <Outlet />
        </div>
      </main>

      {mobileOpen && <div className="mobile-backdrop" onClick={()=>setMobileOpen(false)}/>}
    </div>
  );
}
